const post = {
  slug: 'handling-uncertain-facts-in-scripts',
  cluster: 'scripting',
  eyebrow: 'Scripting',
  title: 'What to do with facts you are not sure about',
  description:
    'Rating what you know before you write it — and why openly stated uncertainty reads as more authoritative than false precision, not less.',
  keywords: [
    'fact checking scripts', 'accuracy youtube', 'sourcing video claims',
    'uncertain facts', 'research accuracy', 'video credibility',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/handling-uncertain-facts-in-scripts.webp',
    alt: 'Research notes sorted into three marked groups by how confidently each claim can be stated.',
  },
  lede:
    'The instinct when you are unsure is to either drop the fact or state it flatly and hope. Both are wrong, and the second one is how channels acquire a slow-burning credibility problem.',
  sections: [
    {
      heading: 'Rate everything before you write it',
      body: [
        {
          type: 'para',
          text: 'Go through your material and put each item into one of three buckets. Do this honestly, before drafting, while you are still able to be objective about it.',
        },
        {
          type: 'list',
          items: [
            '**Confident** — you would defend this. State it plainly, as fact.',
            '**Approximate** — the shape is right, the exact value is not. Use it **only** qualified: "roughly", "around", "more than", "about".',
            '**Unsure** — you believe it but would not stake the video on the number. Do not give it a figure at all. Use the idea qualitatively, or drop it.',
          ],
        },
        {
          type: 'para',
          text: 'The third bucket is the one that gets abused. The temptation is to state an unsure figure anyway because a number is more compelling than a hedge. That is precisely the trade not to make.',
        },
      ],
    },
    {
      heading: 'Why vagueness is not the safe option either',
      body: [
        {
          type: 'para',
          text: 'There is a tension worth naming. A blanket "never be wrong" rule produces prose that is safe because it says nothing — and vague prose is exactly what strips out the figures and names that make a video worth watching.',
        },
        {
          type: 'para',
          text: 'The rating system is what resolves it. It lets you be *maximally* specific on everything you are confident about, precisely because you have been honest about what you are not. You are not choosing between accurate and interesting; you are separating the material so you can be both.',
        },
      ],
    },
    {
      heading: 'Stated uncertainty reads as authority',
      body: [
        {
          type: 'para',
          text: 'This is counter-intuitive and it is worth internalising.',
        },
        {
          type: 'example',
          bad: 'Studies show that forty-one percent of startups fail for this reason.',
          good: 'Nobody has a clean number for this — the definitions differ between studies. What the research agrees on is the direction: it is one of the two most common causes.',
          note: 'The second one sounds like someone who has actually read the research. The first sounds like someone who found a statistic.',
        },
        {
          type: 'para',
          text: 'A viewer who knows the subject spots false precision immediately, and once they have caught one invented figure they discount everything else in the video. A viewer who does not know the subject cannot tell — but the confident hedge still reads as expertise, because that is how experts actually talk.',
        },
      ],
    },
    {
      heading: 'The specific things not to do',
      body: [
        {
          type: 'list',
          items: [
            '**Do not sharpen an approximation.** Turning "around forty percent" into "exactly forty-one percent" because it sounds more authoritative is inventing data.',
            '**Do not attribute to "studies" or "experts" generically.** If you cannot name the source, you do not have one — and the phrase is a tell.',
            '**Do not repeat a widely-circulated fact without checking it.** The most-repeated version of a story is frequently the wrong one; virality selects for tidiness, not accuracy.',
            '**Do not put an uncertain figure in the title or thumbnail.** That is the most visible possible place to be wrong, and it is the part that survives when the video is shared.',
          ],
        },
      ],
    },
    {
      heading: 'Where the line actually is',
      body: [
        {
          type: 'para',
          text: 'The distinction that matters is between inventing **form** and inventing **fact**, and it is narrower than "stay literal".',
        },
        {
          type: 'para',
          text: 'Framing something as a hypothetical is fine — "imagine every container ship stopped tomorrow" invents nothing, because the framing is explicit. Being open about not knowing is fine: "nobody is certain why; the leading explanation is…" is honest and still specific.',
        },
        {
          type: 'para',
          text: 'What is not fine is asserting a study that does not exist, a figure you made up, or a quote nobody said. One word — "imagine", "suppose", "roughly" — makes almost any angle reachable without a false claim.',
        },
        {
          type: 'callout',
          title: 'The practical benefit',
          text: 'An evergreen video that is careful about uncertainty stays right for years. One built on a confidently stated wrong number becomes a liability the moment someone checks — and it stays up, still being recommended.',
        },
      ],
    },
    {
      heading: 'A workable minimum',
      body: [
        {
          type: 'para',
          text: 'Two sources for any figure you state plainly, ideally not derived from each other. One source is a claim; two independent ones is a fact. If both trace back to the same original, you have one.',
        },
        {
          type: 'para',
          text: 'And if a claim is load-bearing for the whole video — the thing the video is *about* — it deserves more scrutiny than the incidental details, because getting it wrong invalidates everything built on it.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'Should I cite sources in the video or the description?',
      a: 'The description is the usual place and costs nothing. Naming a source in the narration is worth doing when the source is itself interesting or when the claim is surprising enough that the viewer will want to know where it came from.',
    },
    {
      q: 'What if I find out a published video is wrong?',
      a: 'A pinned correction is the cheap immediate fix. For an error significant enough to change the video\'s conclusion, replacing it is more honest than leaving it up with a footnote nobody reads.',
    },
  ],
  related: ['specific-script-better-visuals', 'youtube-script-writing', 'youtube-topic-research'],
};

export default post;
